import React, { useState } from 'react'
import { Row, Col } from 'react-flexbox-grid'
import { useTranslation } from 'react-i18next'

import { listProducts } from 'sections/product/gql/query'
import { loadAppConstants, listShops } from 'gql/query'
import listTags from 'gql/query/listTags'
import get from 'helpers/get'
import { Form, Text, Number, Checkbox, DateTimePicker, Select, WeekdayPicker } from 'components/forms'
import { ImageUploaderGraphql } from 'components/Image'
import Button from 'components/Button'
import InfoBox from 'components/InfoBox'
import { ContentBlock } from 'components/ContentBlock'
import Divider from 'components/Divider'
import TagEditor from 'components/TagEditor'

import { getMedia } from '../gql'
import { CustomRow, CustomCol, Label } from './styled'


const Media = ({ appData, data, formData, itemId, onChangeMultiEdit, apiRef, ConfirmButton, ConfirmModal, newRecord, mutations: { createItem, updateItem }, multiEdit }) => {
	const [t, i18n] = useTranslation()
	const img = get(['img', 'key'], data)
	const img16_9 = get(['img16_9', 'key'], data)
	const selectOptions = [
		{
			value: 1,
			label: `SmartCheckout (T2, T2s, S2) 10.1″ ${t('media_section.strings.customer_display')}`,
			uploaderMain: {
				dimensions: { width: 1024, height: 600 }, type: 'Media16_9'
			},
			uploaderSide: {
				dimensions: { width: 408, height: 600 }, type: 'Media'
			}
		}, {
			value: 2,
			label: `SmartCheckout (T3) 10.1″ ${t('media_section.strings.customer_display')}`,
			uploaderMain: {
				dimensions: { width: 1280, height: 800 }, type: 'Media16_9T3'
			},
			uploaderSide: {
				dimensions: { width: 509, height: 800 }, type: 'MediaT3'
			}
		}, {
			value: 3,
			label: 'SelfCheckout (1080x1920)',
			uploaderMain: {
				dimensions: { width: 1080, height: 1920 }, type: 'Media9_16'
			},
			uploaderSide: null
		}
	]
	const [selectVal, setSelectVal] = useState(selectOptions[0])
	const handleSubmit = ({ withProducts, ...values }) => {
		const payload = {
			...values,
			productsId: (withProducts || []).map(({ id }) => id)
		}
		return newRecord ? createItem(payload) : updateItem(payload)
	}
	return(
		<ContentBlock>
			<Form
				apiRef={apiRef}
				smartSubmit={(!newRecord && !multiEdit?.isActive)}
				onChange={(e) => multiEdit?.isActive && onChangeMultiEdit(e, formData)}
				initialValues={formData}
				onSubmit={handleSubmit}
				render={({ formState }) => (
					<>
						<Row>
							<Col xs>
								<Text
									field='name'
									disabled={multiEdit?.isActive}
									label={t('common.fields.name')}
									placeholder={t('common.placeholders.name')}/>
								<Checkbox
									field='isActive'
									label={t('common.fields.isActive')}/>
								<Select
									inline
									style={{ width: '260px' }}
									field='tagId'
									isClearable
									valueField='id'
									labelField='name'
									remoteOptions={{
										query: listTags,
										variables: { type: 'MediaTag' }
									}}
									label={t('media_section.fields.group_tag')}
								/>
								{ConfirmModal()}
								<TagEditor tagType='MediaTag'/>
								<div>
									{!newRecord && <>
										<Number
											label={t('media_section.strings.img_effect')} field='refreshTime'
											placeholder={t('media_section.fields.img_effect_duration')}
											inline
											shrink/>
										<Select
											field='animationType'
											placeholder={t('media_section.fields.img_effect_type')}
											remoteOptions={{
												query: loadAppConstants,
												variables: { type: 76, language: i18n.language }
											}}
											inline
											shrink
										/>
									</>}
								</div>
							</Col>
							<Col xs>
								<DateTimePicker
									field='dateInterval'
									interval
									label={{ from: t('common.fields.from_date'), to: t('common.fields.to_date') }}
									placeholder={{ from: t('common.placeholders.from_date'), to: t('common.placeholders.to_date') }}
								/>
								<Checkbox inline
									field='stayActive'
									label={t('media_section.fields.stay_active')}
								/>
								<DateTimePicker
									field='timeInterval'
									interval
									picker='time'
									label={{ from: t('common.fields.from_time'), to: t('common.fields.to_time') }}
									placeholder={{ from: t('common.placeholders.from_time'), to: t('common.placeholders.to_time') }}
								/>
								{appData.isShopFieldActive &&
									<Select
										label={t('common.fields.shop_affiliation')}
										field='shopId'
										valueField='id'
										labelField='name'
										validate={val => !val && appData.shopAccess ? t('validators.required') : null}
										remoteOptions={{
											query: listShops,
											modifyOptions: (options => {
												return options
													.map(opt => opt.codePrefix ? ({ ...opt, name: `${opt.name} - "${opt.codePrefix}"` }) : opt)
											})
										}}
										isClearable={!appData.shopAccess}
									/>
								}
							</Col>
							<Col xs>
								<WeekdayPicker
									field='weekDays'
									label={t('common.fields.week_days')}
									placeholder={t('common.placeholders.week_days')}
								/>
							</Col>
						</Row>
						{ConfirmButton(formState)}
						{!newRecord && <>
							<Divider visible />
							<Row>
								<Col xs={6}>
									<Label>{t('media_section.strings.device')}</Label>
									<Select
										noForm
										options={selectOptions}
										onChange={val => setSelectVal(val)}
										value={selectVal}
										simpleValue={false}
										disabled={multiEdit?.isActive}
									/>
								</Col>
							</Row>
							<Divider/>
							<CustomRow>
								{selectVal.uploaderMain && <CustomCol xs>
									<ImageUploaderGraphql
										label={t('media_section.fields.img_full_screen')}
										id={itemId}
										getQuery={getMedia}
										field='img16_9'
										parent='Media'
										imgKey={img16_9}
										type={selectVal.uploaderMain.type}
										width={selectVal.uploaderMain.dimensions.width}
										height={selectVal.uploaderMain.dimensions.height}
										elementMaxWidth={300}
									/>
								</CustomCol>}
								{selectVal.uploaderSide && <CustomCol xs>
									<ImageUploaderGraphql
										label={t('media_section.fields.img')}
										parent='Media'
										getQuery={getMedia}
										field='img'
										id={itemId}
										imgKey={img}
										type={selectVal.uploaderSide.type}
										width={selectVal.uploaderSide.dimensions.width}
										height={selectVal.uploaderSide.dimensions.height}
										elementMaxWidth={156}
									/>
								</CustomCol>}
								<CustomCol xs style={{ flexBasis: '300px' }}>
									<Select
										remoteOptions={{
											query: listProducts,
											variables: { params: { limit: 100 } }
										}}
										simpleValue={false}
										withVirtualBody
										disabled={multiEdit?.isActive}
										isMulti
										isSearchable
										label={t('media_section.fields.product_list')}
										placeholder={t('common.placeholders.select_default')}
										field='withProducts'
										maxMenuHeight={250}
										valueField='id'
										labelField='name'
									/>
									<InfoBox text={t('media_section.strings.product_list_info')}/>
								</CustomCol>
							</CustomRow>
						</>}
						{newRecord &&
							<Col type='row'>
								<Button
									type='submit'
									status='primary'
									text={t('common.buttons.create')}/>
							</Col>
						}
					</>
				)}
			/>
		</ContentBlock>
	)
}

export default Media
